import { useValidateAbTestOutput } from "@/resources/ab-test/ab-test.api";
import { toast } from "@/hooks/use-toast";
import { buildAutoTestName } from "@/utils/ab-test-utils";
import { clearSessionDraft } from "@/components/ab-tests/session-draft";
import type { ABTestType, ContentType, IntentType } from "@/types/ab-tests";
import type { CreatedAbTestPayload } from "@/components/ab-tests/create-test-modal.types";

interface UseTestSubmitParams {
  parsedProductId: string;
  selectedBrandId: number | null | undefined;
  selectedFields: ContentType[];
  variantATitle: string;
  variantABullets: Record<string, string>;
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  derivedTestType: ABTestType;
  targetPhrase: string;
  intentFrom: IntentType;
  hypothesis: string;
  lastSource: string | null;
  lastSourceDetail: string;
  appliedScorecardGaps: string[];
  /** AUT-430 (WEB-008): the strategy this test is linked to, three-valued (FR-15, FR-16). */
  strategyLinkId: number | null | undefined;
  onCreateTest: (payload: CreatedAbTestPayload) => void | Promise<void>;
  setIsSaving: (v: boolean) => void;
  setCopyValidation: (v: import("@/types/ai-validation").AiValidationResult | null) => void;
  setSuccessPayload: (v: CreatedAbTestPayload) => void;
  setShowSuccess: (v: boolean) => void;
}

export function useTestSubmit({
  parsedProductId, selectedBrandId, selectedFields,
  variantATitle, variantABullets, variantBTitle, variantBBullets,
  derivedTestType, targetPhrase, intentFrom, hypothesis,
  lastSource, lastSourceDetail, appliedScorecardGaps, strategyLinkId, onCreateTest,
  setIsSaving, setCopyValidation, setSuccessPayload, setShowSuccess,
}: UseTestSubmitParams) {
  const validateAbTestOutputMutation = useValidateAbTestOutput();

  const buildPlanningPayload = (): CreatedAbTestPayload => ({
    testType: derivedTestType, scopeFields: selectedFields, contentType: selectedFields[0] || "title",
    targetPhrase, intentFrom, intentTo: intentFrom,
    hypothesis: hypothesis || "(Draft — hypothesis not yet defined)", name: buildAutoTestName(lastSource, lastSourceDetail, appliedScorecardGaps),
    variantA: { title: selectedFields.includes("title") ? variantATitle : undefined, bullets: selectedFields.filter(f => f.startsWith("bullet-")).map(f => variantABullets[f] || "") },
    variantB: { title: selectedFields.includes("title") ? (variantBTitle || variantATitle) : undefined, bullets: selectedFields.filter(f => f.startsWith("bullet-")).map(f => variantBBullets[f] || variantABullets[f] || "") },
    source: lastSource ?? undefined, sourceDetail: lastSource === "scorecard" && appliedScorecardGaps.length > 0 ? JSON.stringify(appliedScorecardGaps) : lastSourceDetail || undefined, status: "planning",
    seoStrategyRecommendationId: strategyLinkId,
  });

  const handleSaveAsPlanning = async (onClose: () => void) => {
    setIsSaving(true);
    try {
      await onCreateTest(buildPlanningPayload());
      toast({ title: "Test saved to Planning", description: "Your test is parked in Planning — open it from the A/B Tests table to keep editing or launch it." });
      clearSessionDraft(); onClose();
    } catch (_err) { void _err; } finally { setIsSaving(false); }
  };

  const handleSubmit = async () => {
    if (!selectedFields.length) { toast({ title: "Select Fields", description: "Please select at least one listing field to test.", variant: "destructive" }); return; }
    const primaryField = selectedFields[0];
    const bulletFields = selectedFields.filter(f => f.startsWith("bullet-"));
    if (selectedFields.includes("title") && !String(variantBTitle || "").trim()) { toast({ title: "Variant B title", description: "Enter or generate the title for variant B.", variant: "destructive" }); return; }
    for (const f of bulletFields) { if (!String(variantBBullets[f] || "").trim()) { toast({ title: "Variant B bullets", description: `Fill in or generate ${f.replace("bullet-", "Bullet ")}.`, variant: "destructive" }); return; } }
    const numericProductId = Number(parsedProductId);
    setIsSaving(true);
    try {
      if (selectedBrandId && Number.isFinite(numericProductId) && numericProductId > 0) {
        try {
          const valResult = await validateAbTestOutputMutation.mutateAsync({
            productId: numericProductId, brandId: selectedBrandId,
            output: JSON.stringify({ title: selectedFields.includes("title") ? String(variantBTitle || "").trim() : undefined, bulletsByField: Object.fromEntries(bulletFields.map(f => [f, String(variantBBullets[f] || "").trim()])) }),
            featureKey: "ab-test-variant-b-generator",
          });
          setCopyValidation(valResult);
        } catch (_e) { void _e; }
      }
      const payload: CreatedAbTestPayload = {
        testType: derivedTestType, scopeFields: selectedFields, contentType: primaryField, targetPhrase, intentFrom, intentTo: intentFrom,
        hypothesis: hypothesis || "(auto-generated on save)", name: buildAutoTestName(lastSource, lastSourceDetail, appliedScorecardGaps),
        variantA: { title: selectedFields.includes("title") ? String(variantATitle || "").trim() : undefined, bullets: bulletFields.map(f => String(variantABullets[f] || "").trim()) },
        variantB: { title: selectedFields.includes("title") ? String(variantBTitle || "").trim() : undefined, bullets: bulletFields.map(f => String(variantBBullets[f] || "").trim()) },
        source: lastSource ?? undefined, sourceDetail: lastSource === "scorecard" && appliedScorecardGaps.length > 0 ? JSON.stringify(appliedScorecardGaps) : lastSourceDetail || undefined, status: "running",
        seoStrategyRecommendationId: strategyLinkId,
      };
      await onCreateTest(payload);
      clearSessionDraft(); setSuccessPayload(payload); setShowSuccess(true);
    } catch (_e) { void _e; } finally { setIsSaving(false); }
  };

  return { handleSubmit, handleSaveAsPlanning, buildPlanningPayload };
}
